import { useEffect, useState } from 'react'
import { fetchPortfolioUpdates, savePortfolioMemory } from '../../api/portfolio'
import type { PortfolioDailyUpdate } from './types'

function Section({ label, items }: { label: string; items: string[] }) {
  if (!items.length) return null
  return (
    <div>
      <p className="text-swiss-caption uppercase tracking-widest text-swiss-gray-500 mb-1">{label}</p>
      <ul className="space-y-1">
        {items.map((item, idx) => (
          <li key={`${label}-${idx}`} className="text-swiss-small text-swiss-gray-700">
            - {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

interface PortfolioUpdatesProps {
  refreshTick?: number
}

export function PortfolioUpdates({ refreshTick = 0 }: PortfolioUpdatesProps) {
  const [items, setItems] = useState<PortfolioDailyUpdate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [savedId, setSavedId] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchPortfolioUpdates(30)
      setItems(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load updates')
    } finally {
      setLoading(false)
    }
  }

  const saveUpdate = async (update: PortfolioDailyUpdate) => {
    setSavingId(update.id)
    setSavedId(null)
    try {
      const snapshot = [
        `Done: ${update.done.join('; ') || 'none'}`,
        `Next: ${update.next.join('; ') || 'none'}`,
        `Risks: ${(update.risks || []).join('; ') || 'none'}`,
        `Needs help: ${(update.needs_help || []).join('; ') || 'none'}`,
      ].join('\n')

      await savePortfolioMemory({
        title: `Daily Update - ${update.agentId}`,
        content: snapshot,
        summary: `Snapshot from daily update for ${update.agentId}`,
        sourceType: 'daily_update',
        sourceId: update.id,
        tags: [update.agentId, update.groupId || '', 'daily_update'].filter(Boolean),
        createdBy: 'portfolio-updates',
      })
      setSavedId(update.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save memory')
    } finally {
      setSavingId(null)
    }
  }

  useEffect(() => {
    load()
  }, [refreshTick])

  if (loading) {
    return <div className="text-swiss-small text-swiss-gray-500">Loading daily updates...</div>
  }

  if (error) {
    return (
      <div className="border border-swiss-red bg-swiss-off-white p-4">
        <p className="text-swiss-small text-swiss-red mb-2">{error}</p>
        <button onClick={load} className="text-swiss-small underline underline-offset-2 cursor-pointer">
          Retry
        </button>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="border border-swiss-gray-200 bg-swiss-off-white p-6">
        <h2 className="text-swiss-label uppercase tracking-widest text-swiss-gray-500 mb-3">
          Daily Updates
        </h2>
        <p className="text-swiss-small text-swiss-gray-500">
          No updates available yet. Updates are synthesized from current work-order activity.
        </p>
      </div>
    )
  }

  return (
    <div className="border border-swiss-gray-200 bg-swiss-off-white p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-swiss-label uppercase tracking-widest text-swiss-gray-500">
          Daily Updates
        </h2>
        <button onClick={load} className="text-swiss-caption text-swiss-gray-500 underline underline-offset-2 cursor-pointer">
          Refresh
        </button>
      </div>
      <div className="space-y-3">
        {items.map((update) => (
          <div key={update.id} className="border border-swiss-gray-200 bg-swiss-white p-3">
            <div className="flex items-center justify-between mb-2 gap-3">
              <p className="text-swiss-small text-swiss-black">{update.agentId}</p>
              <span className="text-swiss-caption font-swiss-mono text-swiss-gray-400">
                {new Date(update.createdAt).toLocaleString()}
              </span>
            </div>
            <div className="grid gap-3">
              <Section label="Done" items={update.done} />
              <Section label="Next" items={update.next} />
              <Section label="Risks" items={update.risks || []} />
              <Section label="Needs Help" items={update.needs_help || []} />
            </div>
            <div className="mt-3 flex items-center justify-end gap-3">
              {savedId === update.id && (
                <span className="text-swiss-caption text-swiss-gray-500">Saved</span>
              )}
              <button
                onClick={() => saveUpdate(update)}
                disabled={savingId === update.id}
                className="text-swiss-caption underline underline-offset-2 cursor-pointer disabled:opacity-50"
              >
                {savingId === update.id ? 'Saving...' : 'Save to Memory'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
